/**
 * Platform Billing Management (Platform Owner only) — cross-tenant invoices, stats, mark-paid.
 */
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TrendingUp, Building2, Receipt, IndianRupee } from "lucide-react";

export default function PlatformBilling() {
  const { user } = useAuth();

  const [stats, setStats] = useState(null);
  const [invs, setInvs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (user?.is_platform_owner) load(); else setLoading(false); }, [user]);
  const load = async () => {
    setLoading(true);
    try {
      const [s, i] = await Promise.all([api.get("/admin/billing/stats"), api.get("/admin/billing/invoices")]);
      setStats(s.data); setInvs(i.data.invoices || []);
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  const markPaid = async (id) => {
    try { await api.post(`/billing/mark-paid/${id}`); toast.success("Marked paid"); await load(); }
    catch (e) { toast.error("Failed"); }
  };

  if (!user?.is_platform_owner) return <div className="p-6 text-rose-600">Platform-owner access only.</div>;
  if (loading) return <div className="p-6 text-slate-500">Loading…</div>;

  const paid = stats?.invoices?.paid || { count: 0, amount: 0 };
  const pending = stats?.invoices?.pending || { count: 0, amount: 0 };
  const awaiting = stats?.invoices?.awaiting_transfer || { count: 0, amount: 0 };

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Platform</div>
        <h1 className="text-2xl font-bold font-[Outfit]">Billing Management</h1>
        <p className="text-sm text-slate-500 mt-1">Cross-tenant invoice overview + manual mark-paid for bank transfers.</p>
      </div>

      <div className="grid md:grid-cols-4 gap-3">
        <StatBox icon={<Building2 className="w-5 h-5" />} label="Total tenants" value={stats?.total_tenants} sub={`${stats?.active_tenants} active`} tone="cyan" />
        <StatBox icon={<IndianRupee className="w-5 h-5" />} label="MRR (paid)" value={`₹${((paid.amount || 0) / 100).toLocaleString()}`} sub={`${paid.count} invoices`} tone="emerald" />
        <StatBox icon={<Receipt className="w-5 h-5" />} label="Awaiting transfer" value={awaiting.count} sub={`₹${((awaiting.amount || 0) / 100).toLocaleString()}`} tone="amber" />
        <StatBox icon={<TrendingUp className="w-5 h-5" />} label="Pending" value={pending.count} sub={`₹${((pending.amount || 0) / 100).toLocaleString()}`} tone="rose" />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base font-[Outfit]">All invoices (across tenants)</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Tenant</th>
                  <th className="text-left p-3">Plan</th>
                  <th className="text-left p-3">Cycle</th>
                  <th className="text-right p-3">Amount</th>
                  <th className="text-left p-3">Gateway</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invs.map((i) => (
                  <tr key={i.id} className="border-t" data-testid={`platform-inv-${i.id}`}>
                    <td className="p-3 text-slate-400">{new Date(i.created_at).toLocaleDateString()}</td>
                    <td className="p-3 font-medium">{i.tenant_name || i.tenant_id}</td>
                    <td className="p-3">{i.plan_name || i.plan_code || i.plan_id}</td>
                    <td className="p-3 capitalize">{(i.billing_cycle || "").replace("_", " ")}</td>
                    <td className="p-3 text-right font-mono">₹{((i.amount || 0) / 100).toLocaleString()}</td>
                    <td className="p-3 capitalize">{i.gateway}{i.is_mock && <Badge variant="outline" className="text-[9px] ml-1">Mock</Badge>}</td>
                    <td className="p-3">
                      <Badge variant={i.status === "paid" ? "default" : i.status === "awaiting_transfer" ? "outline" : "secondary"}
                             className={i.status === "paid" ? "bg-emerald-600" : ""}>{i.status}</Badge>
                    </td>
                    <td className="p-3 text-right">
                      {i.status !== "paid" && (
                        <Button size="sm" variant="outline" onClick={() => markPaid(i.id)} data-testid={`platform-mark-paid-${i.id}`}>Mark paid</Button>
                      )}
                    </td>
                  </tr>
                ))}
                {invs.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400">No invoices yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatBox({ icon, label, value, sub, tone }) {
  const toneMap = {
    cyan: "bg-cyan-50 text-cyan-700 border-cyan-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <div className={`rounded-lg border p-4 ${toneMap[tone]}`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold opacity-80">{icon} {label}</div>
      <div className="text-2xl font-bold font-[Outfit] mt-1">{value}</div>
      {sub && <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}
